import { useQuery } from '@apollo/client';
import { FontDownload } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router';
import { object, string } from 'yup';
import { useDeleteFont, useUpdateFont } from '~/api/fonts';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { ScrollableRouterTabs } from '~/components/RouterTabs';
import { Tooltip } from '~/components/Tooltip';
import { DeleteButton, SaveButton } from '~/components/button';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { DetailTextField } from '~/components/forms/details';
import { LoadingPane } from '~/components/loading-pane';
import { PageContainer } from '~/components/page-layout';
import {
  SettingDescription,
  SettingInputWrapper,
  SettingLabelWrapper,
  SettingName,
  SettingsBody,
  SettingsData,
  SettingsHead,
} from '~/components/settings';
import { Timestamps } from '~/components/timestamps';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { toDataUri } from '~/lib/file';
import { i18n } from '~/lib/i18n';
import { NetworkFontSettingsDocument } from '../queries/settings.generated';
import { useTabs } from './lib/tabs';

const validationSchema = object({
  fileUri: string().label('Font'),
  name: string()
    .matches(/^[a-z ]+$/i, i18n.t('pages.fonts.update_dialog.validation.letters_only'))
    .required(i18n.t('common.validation.name_required'))
    .trim()
    .label('Name'),
});

export const FontSettings = () => {
  const { t } = useTranslation();
  const { currentNetwork, currentUser } = useAppContext();

  const navigate = useNavigate();

  const link = useLink();

  const tabs = useTabs();

  const params = useParams<{ fontId: string }>();

  const fontId = Number(params.fontId);

  const { data, loading, error } = useQuery(NetworkFontSettingsDocument, {
    variables: { networkId: currentNetwork.id, fontId },
  });

  const [deleteFont] = useDeleteFont();
  const [updateFont] = useUpdateFont();

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const font = data?.network?.font;

  const [selectedFont, setSelectedFont] = useState<File>();

  const initialValues = useMemo(
    () => ({
      fileUri: '',
      name: font?.name ?? '',
    }),
    [font],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: async (values) => {
      await updateFont({
        variables: {
          input: { ...validationSchema.cast(values), id: fontId },
        },
      });
      setSelectedFont(undefined);
      formik.resetForm({ values: { ...values, fileUri: '' } });
    },
    validateOnMount: true,
    validationSchema,
  });

  const { getInputProps, open: selectFile } = useDropzone({
    accept: 'font/*, .ttf, .otf, .woff, .woff2',
    multiple: false,
    onDrop: async (accepted) => {
      if (accepted.length !== 1) return;
      setSelectedFont(accepted[0]);
      const fileUri = await toDataUri(accepted[0]);
      await formik.setFieldValue('fileUri', fileUri, true);
    },
  });

  const handleDelete = useCallback(async () => {
    if (!(await confirmDelete())) return;
    await deleteFont({
      variables: { input: { id: fontId } },
    });
    navigate(link('/fonts'), { replace: true });
  }, [confirmDelete, deleteFont, fontId, link, navigate]);

  // Load the font so it displays correctly on the page
  useEffect(() => {
    if (!font) return;
    const fontFace = new FontFace(font.name, `url(${font.file.uri})`);
    document.fonts.add(fontFace);
    void fontFace.load().catch(() => {
      // Silently ignore font loading errors
    });
  }, [font]);

  if ((!loading && !data) || error) return <Navigate replace to="/not-found" />;

  if (!font) return null;

  return (
    <LoadingPane in={loading && !data} size={80} thickness={4}>
      <>
        <Toolbar
          actions={
            <Box sx={{ display: 'flex', gap: 2 }}>
              <SaveButton
                disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
                type="submit"
                form="font-form"
              />
              <Tooltip title={font.canDelete.reasons?.fullMessages.join(', ')}>
                <span>
                  <DeleteButton disabled={!font.canDelete.value} onClick={handleDelete} />
                </span>
              </Tooltip>
            </Box>
          }
          breadcrumbsLabel={<RouterBreadcrumbs />}
          titleIcon={<FontDownload />}
          titleText={font.name}
        />

        <ScrollableRouterTabs current={t('common.labels.details')} tabs={tabs} />

        <PageContainer>
          <Box
            component="form"
            id="font-form"
            onSubmit={formik.handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <div>
              <SettingsHead>{t('common.labels.general')}</SettingsHead>
              <SettingsBody>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>{t('common.labels.name')}</SettingName>
                    <SettingDescription>
                      {t('pages.fonts.form.fields.name.description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      autoFocus
                      disabled={!font.canUpdate.value}
                      error={!!formik.errors.name}
                      fullWidth
                      helperText={formik.errors.name ?? ' '}
                      name="name"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.name}
                    />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>{t('pages.fonts.form.fields.font_file.label')}</SettingName>
                    <SettingDescription>
                      {t('pages.fonts.form.fields.font_file.description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <input {...getInputProps()} />
                      <Button
                        disabled={!font.canUpdate.value}
                        onClick={selectFile}
                        variant="outlined"
                      >
                        {t('pages.fonts.detail.actions.change_font')}
                      </Button>
                      {selectedFont && <Box>{selectedFont.name}</Box>}
                    </Box>
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData className="last">
                  <SettingLabelWrapper>
                    <SettingName>{t('pages.fonts.form.fields.preview.label')}</SettingName>
                    <SettingDescription>
                      {t('pages.fonts.form.fields.preview.description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <Box
                      sx={{
                        fontFamily: font.name,
                        fontSize: '24px',
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    >
                      The quick brown fox jumps over the lazy dog
                    </Box>
                  </SettingInputWrapper>
                </SettingsData>
              </SettingsBody>
            </div>

            {currentUser.admin && (
              <Timestamps createdAt={font.createdAt} updatedAt={font.updatedAt} />
            )}
          </Box>

          <ConfirmDialog
            {...confirmDeleteProps}
            confirm={t('common.dialogs.permanently_delete')}
            deleteConfirm
            prompt={t('pages.fonts.detail.delete_dialog.prompt')}
            title={t('pages.fonts.detail.delete_dialog.title')}
          />
        </PageContainer>
      </>
    </LoadingPane>
  );
};
